import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { translate, translateLabel, type Locale } from "../i18n/translate";
import { EVENT_GROUPS, eventIdOf, eventPath } from "../system/eventPages";
import { CONSOLE_GROUPS, type NavItem } from "../system/navigation";

// Tour T-64. "Jump anywhere": Ctrl/Cmd+K, or the search button in the header. Lists every
// destination this person may reach (and, inside an event, that event's pages), narrowed by
// what is typed, accents ignored. It is the one way to reach any screen without opening a menu.
type Entry = { key: string; label: string; where: string; to: string };

const plain = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export function CommandPalette({ items, pathname, permissions, locale, open, onClose }: { items: NavItem[]; pathname: string; permissions: string[]; locale: Locale; open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const eventId = eventIdOf(pathname);

  const entries = useMemo<Entry[]>(() => {
    const groupOf = (id: string) => CONSOLE_GROUPS.find((g) => (g.items as readonly string[]).includes(id))?.label;
    const base = items.map((i) => ({
      key: i.id,
      label: translateLabel(locale, i.label),
      where: translateLabel(locale, groupOf(i.id) ?? "Account"),
      to: i.path,
    }));
    const ev = eventId
      ? EVENT_GROUPS.flatMap((g) =>
          g.pages
            .filter((p) => permissions.includes(p.requires))
            .map((p) => ({ key: `ev:${p.screen}`, label: translateLabel(locale, p.label), where: `${translate(locale, "Event")} · ${translateLabel(locale, g.label)}`, to: eventPath(p.screen, eventId) })),
        )
      : [];
    return [...ev, ...base];
  }, [items, eventId, permissions, locale]);

  const hits = entries.filter((e) => plain(`${e.label} ${e.where}`).includes(plain(q)));

  useEffect(() => {
    if (open) {
      setQ("");
      setSel(0);
      window.setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);
  useEffect(() => setSel(0), [q]);

  if (!open) return null;
  const go = (e: Entry | undefined) => {
    if (!e) return;
    onClose();
    navigate(e.to);
  };
  return (
    <div className="palette-veil" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="palette" role="dialog" aria-modal="true" aria-label={translate(locale, "Jump anywhere…")}>
        <input
          ref={input}
          value={q}
          placeholder={translate(locale, "Jump anywhere…")}
          autoComplete="off"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onClose();
            else if (e.key === "ArrowDown") {
              e.preventDefault();
              setSel((s) => Math.min(s + 1, hits.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setSel((s) => Math.max(s - 1, 0));
            } else if (e.key === "Enter") go(hits[sel]);
          }}
        />
        <ul>
          {hits.length === 0 ? <li className="palette-none">{translate(locale, "No results")}</li> : null}
          {hits.map((h, i) => (
            <li key={h.key} aria-selected={i === sel} onMouseEnter={() => setSel(i)} onClick={() => go(h)}>
              {h.label}
              <small>{h.where}</small>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
